import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import request from 'lib/request';
import { caseQueryOptions, fetchCase } from 'lib/evaluation-case/case-query';
import type { ScenarioDomain } from 'lib/evaluation-case/scenario-domains';
import {
  buildAcknowledgement,
  buildPayload,
  changesAreDisjoint,
  mapResponseToForm,
} from './utils';
import type {
  CaseFormValues,
  CaseResponse,
  ReviewStatus,
  ScenarioChoice,
} from './types';

/**
 * The error message a failed write carries: the BE's `errors[].message`, else `fallback`.
 *
 * @param {object} response - A `lib/request` write result that did not succeed.
 * @param {string} fallback - The message when the body names none.
 * @return {string} The message.
 */
function failureMessage(
  response: { type: string; data?: unknown },
  fallback: string
): string {
  const body = (response.type === 'error' ? response.data : undefined) as
    | { errors?: { message: string }[]; error?: { message?: string } }
    | undefined;
  return (
    body?.errors?.map((error) => error.message).join(', ') ||
    body?.error?.message ||
    fallback
  );
}

/**
 * A save refused because the case was written since the page read it (409 STALE_CASE): nothing
 * was saved, and the message says to reload.
 */
export class StaleCaseError extends Error {}

/**
 * Whether a failed write is the server's STALE_CASE refusal.
 *
 * @param {object} response - A `lib/request` write result that did not succeed.
 * @return {boolean} True for a 409 STALE_CASE.
 */
function isStaleCase(response: { type: string; data?: unknown }): boolean {
  const body = (response.type === 'error' ? response.data : undefined) as
    { errors?: { code?: string }[] } | undefined;
  return Boolean(body?.errors?.some((error) => error.code === 'STALE_CASE'));
}

/**
 * The case, as the case page loads it (caseQueryOptions).
 *
 * @param {number | null} caseId - The case id, or null for none (nothing is fetched).
 * @return {UseQueryResult<CaseResponse, Error>} The query.
 */
export function useCase(
  caseId: number | null
): UseQueryResult<CaseResponse, Error> {
  // No retry: a failed load shows its error and the page's Reload is the way back.
  return useQuery<CaseResponse, Error>({
    ...caseQueryOptions<CaseResponse>(caseId),
    retry: 0,
  });
}

/**
 * Creates (no id) or updates the case. An update sends only what changed from `baseline`
 * (buildPayload); with nothing changed it saves nothing and returns the case as it is now. It
 * also sends `expectedUpdatedAt`, the case's updatedAt as the page read it: a case written since
 * is refused, and that throws StaleCaseError.
 *
 * @param {CaseFormValues} values - The form values.
 * @param {number | null} id - The case id, or null to create.
 * @param {CaseFormValues} [baseline] - The values the form started from, for an update.
 * @param {string | null} [expectedUpdatedAt] - The case's updatedAt as the page read it.
 * @return {Promise<CaseResponse>} The saved case.
 */
export async function saveCase(
  values: CaseFormValues,
  id: number | null,
  baseline?: CaseFormValues,
  expectedUpdatedAt?: string | null
): Promise<CaseResponse> {
  const payload = buildPayload(values, id === null ? undefined : baseline);
  if (id !== null && Object.keys(payload.evaluation_case).length === 0) {
    return fetchCase<CaseResponse>(id);
  }
  const response =
    id === null
      ? await request.post<CaseResponse, unknown>(
          '/evaluation_cases',
          payload,
          new AbortController()
        )
      : await request.patch<CaseResponse, unknown>(
          `/evaluation_cases/${id}`,
          expectedUpdatedAt ? { ...payload, expectedUpdatedAt } : payload,
          new AbortController()
        );
  if (response.type !== 'success') {
    const message = failureMessage(response, 'Failed to save case');
    throw isStaleCase(response)
      ? new StaleCaseError(message)
      : new Error(message);
  }
  return response.data;
}

/**
 * Saves the case (saveCase), and on a STALE_CASE refusal retries once when the change made since
 * the page read the case touches none of the fields this user changed: it re-reads the case and
 * sends the same fields against its new updatedAt. An overlapping change, a second 409, or a
 * re-read that fails rethrows the StaleCaseError, so the page shows its Reload notice.
 *
 * @param {CaseFormValues} values - The form values.
 * @param {number | null} id - The case id, or null to create.
 * @param {CaseFormValues} baseline - The values the form started from.
 * @param {string | null} expectedUpdatedAt - The case's updatedAt as the page read it.
 * @return {Promise<CaseResponse>} The saved case.
 */
export async function saveCaseRetryingDisjoint(
  values: CaseFormValues,
  id: number | null,
  baseline: CaseFormValues,
  expectedUpdatedAt: string | null
): Promise<CaseResponse> {
  try {
    return await saveCase(values, id, baseline, expectedUpdatedAt);
  } catch (error) {
    if (!(error instanceof StaleCaseError) || id === null) throw error;
    const fresh = await fetchCase<CaseResponse>(id).catch(() => {
      throw error;
    });
    if (!changesAreDisjoint(values, baseline, mapResponseToForm(fresh)))
      throw error;
    return saveCase(values, id, baseline, fresh.updatedAt);
  }
}

/**
 * `POST /evaluation_cases/:id/clone`.
 *
 * @param {number} id - The case to clone.
 * @return {Promise<CaseResponse>} The clone.
 */
export async function cloneCase(id: number): Promise<CaseResponse> {
  const response = await request.post<CaseResponse, unknown>(
    `/evaluation_cases/${id}/clone`,
    {},
    new AbortController()
  );
  if (response.type !== 'success') {
    throw new Error(failureMessage(response, 'Failed to clone case'));
  }
  return response.data;
}

/**
 * Acknowledges what the review banner showed (`POST /evaluation_cases/:id/review_acknowledgement`).
 *
 * @param {number} id - The case id.
 * @param {ReviewStatus} status - The review status the banner showed.
 * @return {Promise<ReviewStatus>} The case's review status afterwards.
 */
export async function acknowledgeReview(
  id: number,
  status: ReviewStatus
): Promise<ReviewStatus> {
  const response = await request.post<ReviewStatus, unknown>(
    `/evaluation_cases/${id}/review_acknowledgement`,
    buildAcknowledgement(status),
    new AbortController()
  );
  if (response.type !== 'success') {
    throw new Error(
      failureMessage(response, 'Failed to acknowledge the review')
    );
  }
  return response.data;
}

/** One row of a domain's picker endpoint, as the picker reads it. */
export interface PickerRow extends ScenarioChoice {
  updatedAt: string | null;
  /** Retired: archivedAt set, or a Price Deck whose status is 'archived' (the kept assigned one). */
  archived: boolean;
}

/** A picker endpoint's row as sent. */
type PickerResponseRow = Omit<PickerRow, 'archived'> & {
  archivedAt?: string | null;
  status?: string | null;
};

/** The most rows the picker loads; past it, the search narrows the list on the server. */
export const PICKER_PAGE_SIZE = 500;

/** A page of picker rows, and how many match in all. */
export interface PickerPage {
  rows: PickerRow[];
  totalCount: number;
}

/**
 * A domain's pickable scenarios: active ones (archived hidden; for Price Deck, status active),
 * plus the one currently assigned whatever its state. One page of PICKER_PAGE_SIZE; a search
 * term is matched on the server, so a scenario past the first page can still be found. The six
 * Postgres-backed domains come newest-updated first and search their name with `text`. Type
 * Curve and DCA send no sort, which would route them to Elasticsearch, so they come in their
 * default order, newest created first; a search sends `name` (a case-insensitive match anywhere
 * in the name), which is answered from Elasticsearch, by name.
 *
 * @param {ScenarioDomain} domain - The registry domain.
 * @param {number | null} assignedId - The scenario the case has, kept in the list.
 * @param {string} search - The search term; blank for none.
 * @param {AbortSignal} signal - Aborts the request.
 * @return {Promise<PickerPage>} The rows, and the total that match.
 */
export async function fetchPickerRows(
  domain: ScenarioDomain,
  assignedId: number | null,
  search = '',
  signal?: AbortSignal
): Promise<PickerPage> {
  const controller = new AbortController();
  signal?.addEventListener('abort', () => controller.abort());
  const reservoir = domain.key === 'type_curve' || domain.key === 'dca';
  const term = search.trim();
  const query = {
    pageSize: PICKER_PAGE_SIZE,
    ...(reservoir
      ? {}
      : { sortAttribute: 'updated_at', sortDirection: 'DESC' }),
    // The price-deck endpoint permits status only as an array; a scalar is dropped.
    ...(domain.key === 'price_deck'
      ? { status: ['active'] }
      : { archived: false }),
    ...(assignedId !== null && { includeId: assignedId }),
    ...(term && (reservoir ? { name: term } : { text: term })),
  };
  const response = await request.post<
    { results: PickerResponseRow[]; totalCount?: number },
    unknown
  >(domain.queryPath, { query }, controller);
  if (response.type !== 'success') {
    throw new Error(
      failureMessage(response, `Couldn't load ${domain.label} scenarios`)
    );
  }
  const rows = (response.data.results ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    updatedAt: row.updatedAt ?? null,
    areaTags: row.areaTags ?? [],
    areaTagsMoreCount: row.areaTagsMoreCount ?? 0,
    areaTagsAppliesToAll: row.areaTagsAppliesToAll ?? false,
    archived: Boolean(row.archivedAt) || row.status === 'archived',
  }));
  return { rows, totalCount: response.data.totalCount ?? rows.length };
}
